import type { ReactNode } from 'react';
import classnames from 'classnames';
import styles from './Panel.module.scss';

/** Which piece of a panel frame to draw. */
export type PanelVariant = 'header' | 'body' | 'footer';

/** How heavy the frame is. Thick is for modals and anything that needs focus. */
export type PanelFrame = 'thin' | 'thick';

const VARIANT_CLASSES: Record<PanelVariant, string | undefined> = {
  header: styles.header,
  body: styles.body,
  footer: styles.footer,
};

const FRAME_CLASSES: Record<PanelFrame, string | undefined> = {
  thin: styles.thin,
  thick: styles.thick,
};

interface PanelProps {
  children?: ReactNode;
  /**
   * Which piece of the frame to draw. Each piece is complete on its own and
   * knows nothing about other panels; a parent that stacks pieces owns the
   * seams between them.
   */
  variant?: PanelVariant;
  /** Frame weight. */
  frame?: PanelFrame;
  /**
   * Header only: shows the crest on the top rim with this in its shield, an
   * image URL or an icon element. Without it, the header has no crest.
   */
  crest?: string | JSX.Element;
  /** Adds a glow around the panel for emphasis. */
  glow?: boolean;
  className?: string;
}

/**
 * One framed piece of a panel: a header, a body or a footer. It is a plain
 * `div`; the caller wraps it in whatever element gives it meaning (a
 * `section`, an `article`, a `form`).
 */
export function Panel({ children, variant = 'body', frame = 'thin', crest, glow = false, className }: PanelProps): JSX.Element {
  return (
    <div className={classnames(styles.panel, VARIANT_CLASSES[variant], FRAME_CLASSES[frame], glow && styles.glow, className)}>
      {variant === 'header' && crest !== undefined && (
        // Decorative: the header's text names the panel.
        <span className={styles.crest} aria-hidden="true">
          <span className={styles.crestFace}>
            {typeof crest === 'string' ? <img className={styles.crestImage} src={crest} alt="" /> : crest}
          </span>
        </span>
      )}
      {children}
    </div>
  );
}
